import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import React, { useEffect, useState } from 'react';
import { createStore, getStores, updateStore } from '../../api/storeApi';
import AppDialogMessage from '../../components/Common/AppDialogMessage';
import NavBar from '../../components/NavBar/NavBar';
import { useAppDialog } from '../../Hooks/useAppDialog';

const emptyStore = {
    name: '',
    location: '',
    abbreviation: '',
    address: '',
    city: '',
    postal_code: '',
    corporate_no: '',
    notes: '',
    contact: [],
};

const Stores: React.FC = () => {
    const [storeList, setStoreList] = useState<any[]>([]);
    const [storeModal, setStoreModal] = useState(false);
    const [createStoreModal, setCreateStoreModal] = useState(false);
    const [store, setStore] = useState<any>(emptyStore);
    const { dialog, openDialog, closeDialog } = useAppDialog();

    useEffect(() => {
        getStores()
            .then((data) => {
                setStoreList(data);
            })
            .catch((error) => {
                console.log(error);
            });
    }, []);

    const handleModify = async (selectedStore: any) => {
        setStore(selectedStore);
        setStoreModal(true);
    };

    const handleCreate = async () => {
        try {
            const result = await createStore(store);

            setStoreList((prev) => [...prev, result.data]);
            setCreateStoreModal(false);
            setStore(emptyStore);

            openDialog({
                variant: 'success',
                title: 'Store Created',
                message: result.message || 'Store created successfully.',
                confirmText: 'Okay',
                hideCancel: true,
            });
        } catch (error: any) {
            console.log(error);

            openDialog({
                variant: 'error',
                title: 'Create Failed',
                message: error?.message || `Error creating store: ${store.name}`,
                confirmText: 'Close',
                hideCancel: true,
            });
        }
    };

    const handleAddContact = async () => {
        try {
            if (!store?.contact || store?.contact?.length === 0) {
                setStore({
                    ...store,
                    contact: [{ name: '', email: '', phone: '' }],
                });
            } else {
                setStore({
                    ...store,
                    contact: [...store.contact, { name: '', email: '', phone: '' }],
                });
            }
        } catch (error) {
            console.log(error);
            alert('Something went wrong.');
        }
    };

    
    const handleSave = async () => {
        try {
            const result = await updateStore(store);

            setStoreList((prev) =>
                prev.map((item: any) => (item.id === store.id ? store : item))
            );

            setStoreModal(false);
            setStore(emptyStore);

            openDialog({
                variant: 'success',
                title: 'Store Updated',
                message: result.message || 'Store updated successfully.',
                confirmText: 'Okay',
                hideCancel: true,
            });
        } catch (error: any) {
            console.log(error);

            openDialog({
                variant: 'error',
                title: 'Update Failed',
                message: error?.message || `Error updating store: ${store.name}`,
                confirmText: 'Close',
                hideCancel: true,
            });
        }
    };
    const handleDeleteContact = async (id: number) => {
        try {
            setStore({
                ...store,
                contact: store.contact.filter((_: any, i: number) => i !== id),
            });
        } catch (error) {
            console.log(error);
            alert('Something went wrong.');
        }
    };

    const handleContactChange = (index: number, column: string, value: string) => {
        try {
            setStore({
                ...store,
                contact: store.contact.map((item: any, i: number) =>
                    i === index ? { ...item, [column]: value } : item
                ),
            });
        } catch (error) {
            console.log(error);
            alert('Something went wrong.');
        }
    };

    const renderStoreForm = (isCreate = false) => (
        <Stack spacing={2} sx={{ mt: 1 }}>
            <Grid container spacing={2}>
                <Grid size={12}>
                    <TextField
                        fullWidth
                        label="Name"
                        value={store.name || ''}
                        placeholder="Enter name..."
                        onChange={(e) =>
                            setStore({ ...store, name: e.target.value.toUpperCase() || '' })
                        }
                    />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                        fullWidth
                        required
                        label="Location"
                        value={store.location || ''}
                        placeholder="Enter location..."
                        onChange={(e) =>
                            setStore({ ...store, location: e.target.value || '' })
                        }
                    />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                        fullWidth
                        required
                        label="Abbreviation"
                        value={store.abbreviation || ''}
                        placeholder="Abbreviation for BOL NO..."
                        onChange={(e) =>
                            setStore({
                                ...store,
                                abbreviation: e.target.value.toUpperCase() || '',
                            })
                        }
                    />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                        fullWidth
                        label="Address"
                        value={store.address || ''}
                        placeholder="Enter address..."
                        onChange={(e) =>
                            setStore({ ...store, address: e.target.value || '' })
                        }
                    />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                        fullWidth
                        label="City"
                        value={store.city || ''}
                        placeholder="Enter city..."
                        onChange={(e) => setStore({ ...store, city: e.target.value || '' })}
                    />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                        fullWidth
                        label="Postal Code"
                        value={store.postal_code || ''}
                        placeholder="Enter postal code..."
                        onChange={(e) =>
                            setStore({ ...store, postal_code: e.target.value || '' })
                        }
                    />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                        fullWidth
                        label="Corporate No."
                        value={store.corporate_no || ''}
                        placeholder="Enter corporate no..."
                        onChange={(e) =>
                            setStore({ ...store, corporate_no: e.target.value || '' })
                        }
                    />
                </Grid>

                <Grid size={12}>
                    <TextField
                        fullWidth
                        label="Notes"
                        value={store.notes || ''}
                        placeholder="Enter notes..."
                        onChange={(e) => setStore({ ...store, notes: e.target.value || '' })}
                    />
                </Grid>

                <Grid size={12}>
                    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                        <Box
                            sx={{
                                px: 2,
                                py: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                backgroundColor: 'grey.100',
                            }}
                        >
                            <Typography variant="subtitle1" fontWeight={700}>
                                Contacts
                            </Typography>

                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={handleAddContact}
                            >
                                Add Contact
                            </Button>
                        </Box>

                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Phone</TableCell>
                                        <TableCell sx={{ width: 70, fontWeight: 700 }}>Action</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {store?.contact?.length ? (
                                        store.contact.map((contact: any, index: number) => (
                                            <TableRow key={index}>
                                                <TableCell>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        value={contact.name || ''}
                                                        onChange={(e) =>
                                                            handleContactChange(index, 'name', e.target.value)
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        value={contact.email || ''}
                                                        onChange={(e) =>
                                                            handleContactChange(index, 'email', e.target.value)
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        value={contact.phone || ''}
                                                        onChange={(e) =>
                                                            handleContactChange(index, 'phone', e.target.value)
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => handleDeleteContact(index)}
                                                    >
                                                        <DeleteOutlineIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center">
                                                <Typography variant="body2" color="text.secondary">
                                                    No contacts added.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>
            </Grid>

            <DialogActions sx={{ px: 0, pt: 1 }}>
                <Button variant="outlined" onClick={() => {
                    if (isCreate) {
                        setCreateStoreModal(false);
                    } else {
                        setStoreModal(false);
                    }
                    setStore(emptyStore);
                }}>
                    Cancel
                </Button>

                <Button variant="contained" onClick={isCreate ? handleCreate : handleSave}>
                    {isCreate ? 'Create' : 'Save'}
                </Button>
            </DialogActions>
        </Stack>
    );

    return (
        <>
            <NavBar />

            {/* Create Modal */}
            <Dialog
                open={createStoreModal}
                onClose={() => {
                    setCreateStoreModal(false);
                    setStore(emptyStore);
                }}
                fullWidth
                maxWidth="lg"
            >
                <DialogTitle>Create Store</DialogTitle>
                <DialogContent dividers>{renderStoreForm(true)}</DialogContent>
            </Dialog>

            {/* Modify Modal */}
            <Dialog
                open={storeModal}
                onClose={() => {
                    setStoreModal(false);
                    setStore(emptyStore);
                }}
                fullWidth
                maxWidth="lg"
            >
                <DialogTitle>Modify Store</DialogTitle>
                <DialogContent dividers>{renderStoreForm(false)}</DialogContent>
            </Dialog>

            <Box sx={{ p: { xs: 2, md: 2 }, }}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 1 }}
                >
                    <Typography variant="h5" fontWeight={800}>
                        Stores
                    </Typography>

                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => {
                            setStore(emptyStore);
                            setCreateStoreModal(true);
                        }}
                    >
                        Add Store
                    </Button>
                </Stack>

                <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                    <TableContainer sx={{ maxHeight: 'calc(100dvh - 220px)' }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Location</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Address</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>City</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Corporate No.</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Contact</TableCell>
                                    <TableCell sx={{ width: 80, fontWeight: 700 }}>Action</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {storeList.length > 0 ? (
                                    storeList.map((item: any, index: number) => (
                                        <TableRow key={index} hover>
                                            <TableCell>{item.name}</TableCell>
                                            <TableCell>
                                                {item.location} ({item.abbreviation})
                                            </TableCell>
                                            <TableCell>{item.address}</TableCell>
                                            <TableCell>
                                                {item.city}, {item.postal_code}
                                            </TableCell>
                                            <TableCell>{item.corporate_no}</TableCell>
                                            <TableCell>
                                                {item?.contact?.map((contact: any, idx: number) =>
                                                    `${idx === 0 ? '' : ' / '}${contact.name}`
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <IconButton onClick={() => handleModify(item)}>
                                                    <EditOutlinedIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">
                                            <Typography variant="body2" color="text.secondary">
                                                No stores found.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Box>
            <AppDialogMessage
                open={dialog.open}
                title={dialog.title}
                message={dialog.message}
                variant={dialog.variant}
                confirmText={dialog.confirmText}
                cancelText={dialog.cancelText}
                hideCancel={dialog.hideCancel}
                onConfirm={dialog.onConfirm}
                onClose={closeDialog}
            />
        </>
    );
};

export default Stores;